import type { MouseEvent, ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink, type SxProps, type Theme } from '@mui/material';
import { colors, typography as t } from '@/theme/tokens';

interface TextLinkProps {
  /** Internal route to navigate to (rendered as a router link). */
  to?: string;
  /** External href (rendered as an anchor). */
  href?: string;
  /** Open the href in a new tab (only meaningful with `href`). */
  newTab?: boolean;
  /** Click handler (rendered as a button when neither `to` nor `href` is set). */
  onClick?: (e: MouseEvent) => void;
  children: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * The standard "clickable text" affordance: brand-colored, medium weight, with
 * an underline on hover. Renders as a router link (`to`), an anchor (`href`), or
 * a button (`onClick`) so every clickable label across the app reads the same.
 */
export function TextLink({ to, href, newTab, onClick, children, sx }: TextLinkProps) {
  const baseSx: SxProps<Theme> = {
    color: colors.primary.main,
    fontWeight: t.fontWeight.medium,
    ...sx,
  };

  if (to) {
    return (
      <MuiLink component={RouterLink} to={to} underline="hover" sx={baseSx}>
        {children}
      </MuiLink>
    );
  }

  if (href) {
    return (
      <MuiLink
        href={href}
        underline="hover"
        target={newTab ? '_blank' : undefined}
        rel={newTab ? 'noopener noreferrer' : undefined}
        sx={baseSx}
      >
        {children}
      </MuiLink>
    );
  }

  return (
    <MuiLink component="button" type="button" underline="hover" onClick={onClick} sx={baseSx}>
      {children}
    </MuiLink>
  );
}

export default TextLink;
